'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { format, addDays, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { FileUpload } from '@/components/file-upload';
import { toast } from 'sonner';
import { LucideCalendar, LucideClock, LucideCheckCircle, LucideGlobe } from 'lucide-react';
import { PublicLanguageSwitcher } from '@/components/shared/PublicLanguageSwitcher';
import { TimezoneSelector } from '@/components/availability/TimezoneSelector';
import { createClient } from '@/lib/supabase/client';
import {
  getSchedulingPresentation,
  shouldApplyResponse,
  toAgentSummary,
  type SchedulingAgentSummary,
} from './scheduling-utils';

interface Slot {
  id: string;
  start: string; // ISO
  end: string; // ISO
  startLocal: string; // Agent Local (HH:mm)
}

type QueryAgentType = 'interviewer' | 'sales';

interface AvailabilityReadiness {
  ready: boolean;
  setupRequired: boolean;
  reason:
    | 'no_recurring_or_override_availability'
    | 'agent_inactive'
    | 'within_window_no_slots'
    | 'limit_reached';
}

export default function SchedulingPage() {
  const params = useParams();
  const agentId = params.agentId as string;
  const t = useTranslations('scheduling');
  const tc = useTranslations('common');

  const interviewIdFromUrl = typeof params?.interviewId === 'string' ? params.interviewId : '';

  const [timezone, setTimezone] = useState<string>('UTC');
  const [daysWithSlots, setDaysWithSlots] = useState<Set<string>>(new Set());
  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(new Date()));
  const [slots, setSlots] = useState<Slot[]>([]);
  const [agentSummary, setAgentSummary] = useState<SchedulingAgentSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [readiness, setReadiness] = useState<AvailabilityReadiness | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const [isBooked, setIsBooked] = useState(false);
  const [bookingData, setBookingData] = useState<any>(null);
  const [resumeData, setResumeData] = useState<{ storagePath: string; parsedText: string } | null>(
    null,
  );

  const [formData, setFormData] = useState({
    candidate_name: '',
    candidate_email: '',
  });

  const handleResumeComplete = useCallback(
    (result: { storagePath: string; parsedText: string; candidateName?: string }) => {
      setResumeData(result);
      if (result.candidateName && !formData.candidate_name.trim()) {
        setFormData((prev) => ({ ...prev, candidate_name: result.candidateName! }));
      }
    },
    [formData.candidate_name],
  );
  const latestSlotsRequestRef = useRef(0);
  const refreshIntervalRef = useRef<number | null>(null);

  const [shareContext, setShareContext] = useState({
    interviewId: '',
    shareCode: '',
    role: '',
    companyName: '',
    interviewTitle: '',
  });

  // Detect browser timezone
  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(tz || 'UTC');
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryAgentType = searchParams.get('agentType') as QueryAgentType | null;
    const queryFlowName = searchParams.get('flowName');
    setShareContext({
      interviewId: searchParams.get('interviewId') || interviewIdFromUrl || '',
      shareCode: searchParams.get('shareCode') || '',
      role: searchParams.get('role') || '',
      companyName: searchParams.get('companyName') || '',
      interviewTitle: searchParams.get('interviewTitle') || '',
    });

    if (queryAgentType === 'interviewer' || queryAgentType === 'sales') {
      setAgentSummary((prev) =>
        toAgentSummary({
          id: agentId,
          name: prev?.name || 'AI Agent',
          agent_type: queryAgentType,
          flow_name: queryFlowName,
        }),
      );
    }
  }, [interviewIdFromUrl, agentId]);

  // Fetch 7 days of dates for selection
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(startOfDay(new Date()), i)),
    [],
  );
  const presentation = useMemo(() => getSchedulingPresentation(agentSummary, t), [agentSummary, t]);
  const isInterviewMeeting = agentSummary?.agent_type !== 'sales';

  // Fetch slots for selected date
  const fetchSlots = useCallback(
    async (date: Date) => {
      const requestId = latestSlotsRequestRef.current + 1;
      latestSlotsRequestRef.current = requestId;

      setIsLoading(true);
      try {
        const dateStr = format(date, 'yyyy-MM-dd');
        const response = await fetch(`/api/bookings?agentId=${agentId}&date=${dateStr}`);
        if (!response.ok) throw new Error('Failed to fetch slots');
        const data = await response.json();

        if (!shouldApplyResponse(requestId, latestSlotsRequestRef.current)) {
          return;
        }

        setSlots(data.slots || []);
        setReadiness(data.readiness || null);
        setAgentSummary(toAgentSummary(data.agent));
        // Keep the user's selection across background refreshes (30s interval,
        // realtime booking events, tab refocus) — only clear it if the slot is
        // genuinely gone (booked by someone else or removed). Clearing it
        // unconditionally would unmount the booking form out from under a
        // candidate who is mid-way through filling it out.
        setSelectedSlot((prev) => {
          if (!prev) return prev;
          const stillAvailable = data.slots?.some((slot) => slot.id === prev.id) ?? false;
          return stillAvailable ? prev : null;
        });
        setLiveAnnouncement(
          data.slots?.length
            ? t('slotsAvailable', { count: data.slots.length, date: format(date, 'MMMM d') })
            : t('noSlotsForDate', { date: format(date, 'MMMM d') }),
        );
      } catch (error) {
        console.error(error);
        toast.error(t('failedToLoadSlots'));
        setReadiness(null);
        setLiveAnnouncement(t('unableToRefresh'));
      } finally {
        if (shouldApplyResponse(requestId, latestSlotsRequestRef.current)) {
          setIsLoading(false);
        }
      }
    },
    [agentId, t],
  );

  useEffect(() => {
    fetchSlots(selectedDate);
  }, [selectedDate, fetchSlots]);

  // Mark which of the 7 visible days have openings so candidates can find an
  // available day at a glance instead of clicking through each one. Purely
  // additive — the day strip works the same without it.
  const fetchWeekAvailability = useCallback(async () => {
    try {
      const start = format(days[0], 'yyyy-MM-dd');
      const end = format(days[days.length - 1], 'yyyy-MM-dd');
      const response = await fetch(
        `/api/bookings?agentId=${agentId}&mode=availability&startDate=${start}&endDate=${end}`,
      );
      if (!response.ok) return;
      const data = await response.json();
      const withSlots = Object.entries(data.availabilityByDate ?? {})
        .filter(([, count]) => (count as number) > 0)
        .map(([dateStr]) => dateStr);
      setDaysWithSlots(new Set(withSlots));
    } catch {
      // Dots are progressive enhancement — the strip still works without them.
    }
  }, [agentId, days]);

  useEffect(() => {
    void fetchWeekAvailability();
  }, [fetchWeekAvailability]);

  useEffect(() => {
    const client = createClient();
    const channel = client
      .channel(`booking-slots-${agentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings',
          filter: `agent_id=eq.${agentId}`,
        },
        () => {
          void fetchSlots(selectedDate);
        },
      )
      .subscribe();

    const onVisibility = () => {
      if (!document.hidden) {
        void fetchSlots(selectedDate);
      }
    };

    document.addEventListener('visibilitychange', onVisibility, { passive: true });

    refreshIntervalRef.current = window.setInterval(() => {
      void fetchSlots(selectedDate);
    }, 30000);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (refreshIntervalRef.current !== null) {
        window.clearInterval(refreshIntervalRef.current);
      }
      void client.removeChannel(channel);
    };
  }, [agentId, fetchSlots, selectedDate]);

  const handleBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) return;

    if (isInterviewMeeting && !resumeData) {
      toast.error(t('resumeRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      const bookingPayload: Record<string, unknown> = {
        agent_id: agentId,
        ...formData,
        scheduled_at: selectedSlot.start,
        timezone_at_booking: timezone,
        interview_id: shareContext.interviewId || undefined,
        share_code: shareContext.shareCode || undefined,
      };

      if (isInterviewMeeting && resumeData) {
        bookingPayload.resume_storage_path = resumeData.storagePath;
        bookingPayload.resume_parsed_text = resumeData.parsedText;
      }

      const response = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('failedToBook'));
      }

      const data = await response.json();
      setBookingData(data.booking);
      setAgentSummary(
        toAgentSummary({
          id: agentId,
          name: agentSummary?.name || 'AI Agent',
          agent_type: data.booking?.agent_type,
          flow_name: data.booking?.flow_name,
        }),
      );
      setIsBooked(true);
      toast.success(t('bookingConfirmed'));
      setLiveAnnouncement(t('bookingConfirmed'));
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isBooked && bookingData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <LucideCheckCircle className="w-12 h-12 text-primary mx-auto mb-2" />
            <CardTitle className="text-2xl font-bold">{presentation.confirmationTitle}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {presentation.contextLabel} {t('session')}
              </p>
              <p className="font-medium text-lg">{bookingData.candidate_name}</p>
              <div className="flex items-center gap-2 text-muted-foreground mt-2">
                <LucideCalendar className="w-4 h-4" />
                <span>{format(new Date(bookingData.scheduled_at), 'EEEE, MMMM d, yyyy')}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <LucideClock className="w-4 h-4" />
                <span>
                  {format(new Date(bookingData.scheduled_at), 'h:mm a')} ({timezone})
                </span>
              </div>
            </div>
            <p className="text-center text-sm text-muted-foreground">
              {t('confirmationEmailSent', { email: bookingData.candidate_email })}
            </p>
            {bookingData.resume_storage_path ? (
              <p className="text-center text-xs text-muted-foreground">{t('resumeAttached')}</p>
            ) : null}
            <div className="flex flex-col gap-2">
              <Button asChild className="w-full">
                <a href={bookingData.video_url} target="_blank" rel="noopener noreferrer">
                  {t('joinInterviewRoom')}
                </a>
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={async () => {
                  try {
                    const response = await fetch('/api/bookings/ics', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        booking_id: bookingData.id,
                        cancel_token: bookingData.cancel_token,
                      }),
                    });
                    if (!response.ok) throw new Error('Failed to generate calendar invite');
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `interview-${bookingData.id}.ics`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  } catch (err) {
                    toast.error(t('failedCalendarInvite'));
                  }
                }}
              >
                {t('addToCalendar')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl p-4 py-8">
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {liveAnnouncement}
      </div>
      <div className="flex justify-end mb-2">
        <PublicLanguageSwitcher />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">{presentation.title}</h1>
          {agentSummary ? (
            <p className="mb-2 text-sm text-muted-foreground">
              {presentation.contextLabel} Agent:{' '}
              <span className="font-medium text-foreground">{agentSummary.name}</span>
              <span className="ml-2 font-mono text-xs">{agentSummary.flow_name}</span>
            </p>
          ) : null}
          {shareContext.role || shareContext.companyName || shareContext.interviewTitle ? (
            <div className="mb-4 rounded-lg border bg-muted/30 p-4">
              <p className="text-sm text-muted-foreground">{t('interviewContext')}</p>
              <p className="font-medium">
                {shareContext.role
                  ? `${shareContext.role}${shareContext.companyName ? ` at ${shareContext.companyName}` : ''}`
                  : shareContext.interviewTitle || t('aiInterviewSession')}
              </p>
            </div>
          ) : null}
          <p className="text-muted-foreground mb-8">{presentation.description}</p>

          <div className="flex items-center gap-2 mb-4 text-sm font-medium">
            <LucideGlobe className="w-4 h-4 text-muted-foreground" />
            <TimezoneSelector value={timezone} onChange={setTimezone} compact />
          </div>

          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {days.map((day) => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const isSelected = dateStr === format(selectedDate, 'yyyy-MM-dd');
              const hasSlots = daysWithSlots.has(dateStr);
              return (
                <Button
                  key={day.toISOString()}
                  variant={isSelected ? 'default' : 'outline'}
                  className="flex-col h-16 w-16 min-w-16"
                  onClick={() => setSelectedDate(day)}
                  data-has-slots={hasSlots ? 'true' : 'false'}
                  aria-pressed={isSelected}
                >
                  <span className="text-[10px] uppercase font-bold">{format(day, 'EEE')}</span>
                  <span className="text-lg leading-none">{format(day, 'd')}</span>
                  <span
                    aria-hidden="true"
                    className={`mt-1 h-1 w-1 rounded-full ${hasSlots ? 'bg-primary' : 'bg-transparent'}`}
                  />
                  {hasSlots && <span className="sr-only">{t('dayHasSlots')}</span>}
                </Button>
              );
            })}
          </div>

          <div
            className="space-y-2 max-h-[400px] overflow-y-auto pr-2"
            aria-busy={isLoading ? 'true' : 'false'}
          >
            <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-2">
              {t('availableSlots')}
            </h3>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-md" />
              ))
            ) : slots.length > 0 ? (
              slots.map((slot) => {
                const startTime = toZonedTime(new Date(slot.start), timezone);
                return (
                  <Button
                    key={slot.id}
                    variant={selectedSlot?.id === slot.id ? 'default' : 'outline'}
                    className="w-full justify-start h-12"
                    onClick={() => setSelectedSlot(slot)}
                    data-slot="true"
                    aria-pressed={selectedSlot?.id === slot.id}
                  >
                    <LucideClock className="w-4 h-4 mr-2 opacity-50" />
                    {format(startTime, 'h:mm a')}
                  </Button>
                );
              })
            ) : readiness?.setupRequired ? (
              <Card className="border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20">
                <CardContent className="pt-6">
                  <p className="font-medium text-amber-900 dark:text-amber-100">
                    {t('agentNotReady')}
                  </p>
                  <p className="text-sm text-amber-800/90 dark:text-amber-200/90 mt-2">
                    {t('agentNotReadyDesc')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <p className="text-muted-foreground py-8 text-center italic">{t('noSlotsDate')}</p>
            )}
          </div>
        </div>

        <div>
          {selectedSlot ? (
            <Card className="sticky top-8">
              <CardHeader>
                <CardTitle>{t('completeBooking')}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleBooking} className="space-y-4">
                  <div className="bg-muted p-4 rounded-lg mb-4">
                    <div className="flex items-center gap-2 font-medium">
                      <LucideCalendar className="w-4 h-4" />
                      <span>{format(new Date(selectedSlot.start), 'MMMM d, yyyy')}</span>
                    </div>
                    <div className="flex items-center gap-2 font-medium mt-1">
                      <LucideClock className="w-4 h-4" />
                      <span>
                        {format(toZonedTime(new Date(selectedSlot.start), timezone), 'h:mm a')} (
                        {timezone})
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="candidate_name">{t('fullName')}</Label>
                    <Input
                      id="candidate_name"
                      name="candidate_name"
                      required
                      value={formData.candidate_name}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, candidate_name: e.target.value }))
                      }
                      placeholder={t('fullNamePlaceholder')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="candidate_email">{t('emailAddress')}</Label>
                    <Input
                      id="candidate_email"
                      name="candidate_email"
                      type="email"
                      required
                      value={formData.candidate_email}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, candidate_email: e.target.value }))
                      }
                      placeholder={t('emailPlaceholder')}
                    />
                  </div>

                  {isInterviewMeeting ? (
                    <div className="space-y-2">
                      <Label>{t('candidateResume')}</Label>
                      <FileUpload entityType="resume" autoSave onComplete={handleResumeComplete} />
                    </div>
                  ) : null}

                  <Button type="submit" className="w-full h-12" disabled={isSubmitting}>
                    {isSubmitting ? t('confirming') : t('confirmBooking')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : readiness?.setupRequired ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] border-2 border-dashed rounded-lg p-8 text-center">
              <LucideCalendar className="w-12 h-12 text-amber-500 mb-4 opacity-60" />
              <p className="font-medium text-foreground">{t('bookingSetupRequired')}</p>
              <p className="text-muted-foreground mt-2 max-w-sm">{t('bookingSetupRequiredDesc')}</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] border-2 border-dashed rounded-lg p-8 text-center">
              <LucideCalendar className="w-12 h-12 text-muted-foreground mb-4 opacity-20" />
              <p className="text-muted-foreground">{t('selectTimeSlot')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
